import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiAlertTriangle,
  FiCamera,
  FiEye,
  FiMonitor,
  FiSmartphone,
  FiCheckCircle,
  FiArrowLeft,
  FiShield,
  FiClock,
  FiList,
} from "react-icons/fi";
import ThemeSelector from "../components/ThemeSelector";

const rules = [
  {
    icon: <FiCamera />,
    title: "Webcam Monitoring",
    desc: "Your webcam will be active throughout the assessment. Periodic snapshots will be taken and reviewed by the administrator.",
    image: null,
  },
  {
    icon: <FiEye />,
    title: "Face Must Be Clearly Visible",
    desc: "You must remain visible on camera at all times. Ensure your face is well-lit, centred, and fully in frame throughout the assessment.",
    image: "/images/rule-face-visible.png",
    imageLabel: "Correct position",
  },
  {
    icon: <FiSmartphone />,
    title: "No Mobile Devices on Screen",
    desc: "Use of a mobile phone, tablet, or any external device during the test is strictly prohibited and will be automatically detected by AI.",
    image: "/images/rule-no-mobile.png",
    imageLabel: "Phone detected = violation",
  },
  {
    icon: <FiEye />,
    title: "No Left or Right Head Movement",
    desc: "Keep your head facing the screen at all times. Looking left, right, or turning away will be flagged as a violation by the detection system.",
    image: "/images/rule-no-head-turn.png",
    imageLabel: "Head turned = violation",
  },
  {
    icon: <FiAlertTriangle />,
    title: "No Hands on Face",
    desc: "Do not cover your face with your hands, rest your chin on your palm, or obstruct your facial features in any way during the assessment.",
    image: "/images/rule-no-hands.png",
    imageLabel: "Face obstructed = violation",
  },
  {
    icon: <FiMonitor />,
    title: "No Tab Switching",
    desc: "Switching to another tab, browser window, or application during the assessment will be recorded as a violation.",
    image: null,
  },
];

/* ─── Loading Overlay (replaces 3-2-1 countdown) ───── */

function LoadingOverlay({ onComplete }) {
  useEffect(() => {
    const t = setTimeout(onComplete, 2400);
    return () => clearTimeout(t);
  }, [onComplete]);

  return (
    <motion.div
      className="countdown-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="loading-spinner-container">
        <svg className="loading-spinner-ring" viewBox="0 0 100 100">
          <circle
            cx="50" cy="50" r="42"
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="3"
            strokeDasharray="4 8"
          />
          <circle
            cx="50" cy="50" r="42"
            fill="none"
            stroke="var(--color-highlight, #6366f1)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="80 200"
            className="loading-spinner-arc"
          />
        </svg>
      </div>
      <motion.p
        className="loading-spinner-text"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        Starting Assessment...
      </motion.p>
    </motion.div>
  );
}

/* ─── Main Rules Page ──────────────────────────────── */

export default function Rules() {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [checkingCamera, setCheckingCamera] = useState(false);

  const handleStart = async () => {
    setCameraError("");
    setCheckingCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false,
      });
      // Camera access granted — stop the test stream and proceed
      stream.getTracks().forEach((t) => t.stop());
      setCheckingCamera(false);
      setShowCountdown(true);
    } catch (err) {
      setCheckingCamera(false);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setCameraError(
          "Camera access is required to start the assessment. Please allow camera permission in your browser settings and try again."
        );
      } else if (err.name === "NotFoundError") {
        setCameraError(
          "No camera detected on this device. A working webcam is required for the proctored assessment."
        );
      } else {
        setCameraError(
          `Camera error: ${err.message}. Please ensure your webcam is connected and accessible.`
        );
      }
    }
  };

  const handleCountdownComplete = useCallback(() => {
    navigate("/quiz");
  }, [navigate]);

  return (
    <div className="rules-page">
      <ThemeSelector />

      <AnimatePresence>
        {showCountdown && (
          <LoadingOverlay onComplete={handleCountdownComplete} />
        )}
      </AnimatePresence>

      {!showCountdown && (
        <>
          <button className="logout-btn" onClick={() => navigate("/welcome")}>
            <FiArrowLeft /> Back
          </button>

          <motion.div
            className="rules-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="rules-header">
              <div className="rules-shield-icon">
                <FiShield />
              </div>
              <h1>Assessment Rules & Proctoring</h1>
              <p className="rules-subtitle">
                Please read the following rules carefully before starting.
                Violations may result in disqualification.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div style={{
                background: "linear-gradient(135deg, rgba(99,102,241,0.15), rgba(99,102,241,0.05))",
                border: "1px solid rgba(99,102,241,0.35)",
                borderRadius: "12px",
                padding: "16px 18px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}>
                <FiClock size={28} style={{ color: "var(--color-highlight, #6366f1)", flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: "22px", fontWeight: 800, color: "var(--text-primary)", lineHeight: 1.1 }}>
                    45 Minutes
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "3px" }}>
                    Total duration
                  </div>
                </div>
              </div>
              <div style={{
                background: "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))",
                border: "1px solid rgba(34,197,94,0.35)",
                borderRadius: "12px",
                padding: "16px 18px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}>
                <FiList size={28} style={{ color: "#22c55e", flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: "22px", fontWeight: 800, color: "var(--text-primary)", lineHeight: 1.1 }}>
                    45 Questions
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "3px" }}>
                    Mix of short &amp; scenario questions covering all areas
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="monitored-banner"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <FiEye className="monitored-icon" />
              <div>
                <strong>You are being monitored</strong>
                <p>
                  This assessment uses AI-powered proctoring. Your webcam,
                  screen activity, and browser behaviour will be monitored and
                  recorded throughout the test.
                </p>
              </div>
            </motion.div>

            <div className="rules-list">
              {rules.map((rule, i) => (
                <motion.div
                  key={i}
                  className="rule-item"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.08, duration: 0.4, ease: "easeOut" }}
                >
                  <div className="rule-top-row">
                    <div className="rule-icon">{rule.icon}</div>
                    <div className="rule-content">
                      <h3>{rule.title}</h3>
                      <p>{rule.desc}</p>
                    </div>
                  </div>
                  {rule.image && (
                    <div className="rule-image-container">
                      <img
                        src={rule.image}
                        alt={rule.imageLabel}
                        className="rule-image"
                      />
                      <span className="rule-image-label">{rule.imageLabel}</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.div
              className="rules-agreement"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.4 }}
            >
              <label className="agreement-checkbox">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span className="checkmark" />
                <span className="agreement-text">
                  I have read and understood the rules. I agree to be monitored
                  during this assessment and understand that any violation may
                  lead to disqualification.
                </span>
              </label>

              {cameraError && (
                <motion.div
                  className="camera-error-banner"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FiAlertTriangle className="camera-error-icon" />
                  <span>{cameraError}</span>
                </motion.div>
              )}

              <motion.button
                className={`btn-primary btn-large rules-start-btn ${!agreed || checkingCamera ? "btn-disabled" : ""}`}
                disabled={!agreed || checkingCamera}
                onClick={handleStart}
                whileHover={agreed && !checkingCamera ? { scale: 1.02 } : {}}
                whileTap={agreed && !checkingCamera ? { scale: 0.98 } : {}}
              >
                {checkingCamera ? (
                  <>Checking Camera Access...</>
                ) : (
                  <><FiCheckCircle /> I Agree — Start Assessment</>
                )}
              </motion.button>
            </motion.div>
          </motion.div>
        </>
      )}
    </div>
  );
}
